import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, Heart, Mic, BookOpen, Building2, Check } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const OfferingsPage = () => {
  const circleThemes = [
    "Trauma-responsive embodiment",
    "Pleasure, desire, and consent",
    "Boundaries and body sovereignty",
    "Sexual healing after abuse",
    "Reclaiming sensation",
    "The body as resistance"
  ];

  const educationTopics = [
    "Embodiment basics for survivors",
    "Consent as somatic practice",
    "Pleasure as public health",
    "The politics of sensation",
    "Body mapping and reclamation",
    "Integrating somatic tools into daily life"
  ];

  const coachingFeatures = [
    "Personalized somatic assessment",
    "Tailored embodiment practices",
    "Consent-centered framework",
    "Integration of pleasure and healing",
    "Ongoing support between sessions",
    "Flexible virtual scheduling"
  ];

  const institutionalTopics = [
    "Trauma-informed care fundamentals",
    "Embodied approaches to patient care",
    "Consent culture in healthcare settings",
    "Supporting survivors in clinical contexts",
    "The intersection of race, trauma, and embodiment",
    "Pleasure as justice in health frameworks"
  ];

  return (
    <div data-testid="offerings-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing" data-testid="offerings-hero">
        <div className="section-container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.15 } }
            }}
            className="max-w-3xl"
          >
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4"
            >
              Offerings
            </motion.p>
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-heading text-4xl md:text-5xl text-text-primary mb-8 leading-tight"
            >
              Pathways to{" "}
              <span className="italic text-brand-accent">Restoration</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="font-body text-lg text-text-secondary leading-relaxed"
            >
              Whether you seek community connection, personalized support, or institutional 
              transformation, I offer multiple pathways to engage with this work. Each offering 
              is rooted in consent, embodiment, and the belief that healing is both personal 
              and political.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* For Individuals & Communities */}
      <section className="section-spacing bg-bg-secondary" data-testid="individual-offerings">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4">
              For Individuals & Communities
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary">
              Personal Healing Pathways
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Somale Circles */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card"
              data-testid="offering-circles"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-brand-accent/10 text-brand-accent">
                  <Users size={28} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl text-text-primary mb-2">
                    Somale Circles
                  </h3>
                  <p className="font-body text-sm text-text-secondary">
                    Community Healing Gatherings
                  </p>
                </div>
              </div>

              <p className="font-body text-text-secondary leading-relaxed mb-6">
                Monthly community gatherings designed specifically for Black survivors. 
                These intimate circles create space for collective healing through embodiment 
                practices, shared wisdom, and the restoration of connection — to self, 
                to body, to community.
              </p>

              <div className="mb-6">
                <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-4">
                  Themes Include:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {circleThemes.map((theme, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check size={14} className="text-brand-accent flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{theme}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link to="/book" className="btn-secondary inline-block" data-testid="circles-book-btn">
                Join a Circle
              </Link>
            </motion.div>

            {/* One-on-One Coaching */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card"
              data-testid="offering-coaching"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-brand-accent/10 text-brand-accent">
                  <Heart size={28} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl text-text-primary mb-2">
                    One-on-One Coaching
                  </h3>
                  <p className="font-body text-sm text-text-secondary">
                    Personalized Somatic Support
                  </p>
                </div>
              </div>

              <p className="font-body text-text-secondary leading-relaxed mb-6">
                For those seeking deeper, individualized work, I offer one-on-one coaching 
                sessions. Together, we explore your unique relationship with your body, 
                develop personalized embodiment practices, and work toward reclaiming 
                sensation, pleasure, and sovereignty.
              </p>

              <div className="mb-6">
                <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-4">
                  Features:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {coachingFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check size={14} className="text-brand-accent flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link to="/book" className="btn-primary inline-block" data-testid="coaching-book-btn">
                Book a Session
              </Link>
            </motion.div>

            {/* Somatic Education */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card lg:col-span-2"
              data-testid="offering-education"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 bg-brand-accent/10 text-brand-accent">
                      <BookOpen size={28} />
                    </div>
                    <div>
                      <h3 className="font-heading text-2xl text-text-primary mb-2">
                        Somatic Education
                      </h3>
                      <p className="font-body text-sm text-text-secondary">
                        Monthly Subscription via Skool
                      </p>
                    </div>
                  </div>

                  <p className="font-body text-text-secondary leading-relaxed mb-6">
                    An ongoing educational journey through monthly workshops and community 
                    learning. This subscription-based offering provides regular access to 
                    somatic education, embodiment practices, and a supportive community 
                    of fellow travelers on the healing path.
                  </p>

                  <Link to="/book" className="btn-secondary inline-block" data-testid="education-book-btn">
                    Learn More
                  </Link>
                </div>

                <div>
                  <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-4">
                    Topics Covered:
                  </p>
                  <div className="space-y-2">
                    {educationTopics.map((topic, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Check size={14} className="text-brand-accent flex-shrink-0" />
                        <span className="text-text-secondary">{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Institutions */}
      <section className="section-spacing" data-testid="institutional-offerings">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4">
              For Health Systems & Institutions
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary">
              Organizational Transformation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Training for Providers */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card"
              data-testid="offering-training"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-brand-accent/10 text-brand-accent">
                  <Building2 size={28} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl text-text-primary mb-2">
                    Training for Providers
                  </h3>
                  <p className="font-body text-sm text-text-secondary">
                    Professional Development
                  </p>
                </div>
              </div>

              <p className="font-body text-text-secondary leading-relaxed mb-6">
                Comprehensive training programs for healthcare providers, mental health 
                professionals, and organizational leaders. These trainings equip 
                professionals with trauma-responsive, embodied approaches to supporting 
                survivors in clinical and organizational contexts.
              </p>

              <div className="mb-6">
                <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-4">
                  Topics Include:
                </p>
                <div className="space-y-2">
                  {institutionalTopics.slice(0, 3).map((topic, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check size={14} className="text-brand-accent flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{topic}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link to="/book" className="btn-secondary inline-block" data-testid="training-book-btn">
                Inquire About Training
              </Link>
            </motion.div>

            {/* Speaking Engagements */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card"
              data-testid="offering-speaking"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-brand-accent/10 text-brand-accent">
                  <Mic size={28} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl text-text-primary mb-2">
                    Speaking Engagements
                  </h3>
                  <p className="font-body text-sm text-text-secondary">
                    Keynotes, Panels & Workshops
                  </p>
                </div>
              </div>

              <p className="font-body text-text-secondary leading-relaxed mb-6">
                I bring the conversation about embodied healing, reproductive justice, 
                and trauma-responsive care to conferences, universities, healthcare 
                organizations, and community events. Each engagement is tailored to 
                your audience's needs and context.
              </p>

              <div className="mb-6">
                <p className="font-body text-xs uppercase tracking-wider text-text-secondary mb-4">
                  Topics Include:
                </p>
                <div className="space-y-2">
                  {institutionalTopics.slice(3).map((topic, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check size={14} className="text-brand-accent flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{topic}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Link to="/book" className="btn-primary inline-block" data-testid="speaking-book-btn">
                Book a Speaking Engagement
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Press Bio Section */}
      <section className="section-spacing bg-bg-secondary" data-testid="press-bio">
        <div className="section-container">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="font-body text-xs uppercase tracking-[0.3em] text-brand-accent mb-4">
                Press Bio
              </p>
              <h2 className="font-heading text-3xl text-text-primary mb-8">
                For Media & Event Organizers
              </h2>

              <div className="card">
                <p className="font-body text-text-secondary leading-relaxed mb-6">
                  <span className="text-text-primary font-medium">Inas-Khalidah Mahdi</span> is 
                  a survivor of sexual violence, an embodied researcher, and a healing justice 
                  practitioner dedicated to supporting Black survivors of gender-based violence, 
                  racialized harm, and sexual trauma. She is the founder of{" "}
                  <span className="text-brand-accent">Somale Restoration Collective</span> and 
                  is currently pursuing doctoral research at Johns Hopkins Bloomberg School of 
                  Public Health, where she studies embodied sexology with a focus on child 
                  sexual abuse survivors.
                </p>

                <p className="font-body text-text-secondary leading-relaxed mb-6">
                  Her work spans community healing circles, one-on-one coaching, somatic 
                  education, provider training, and speaking engagements — all rooted in the 
                  belief that embodied, consent-centered practices are essential public health 
                  interventions. Inas-Khalidah's mission centers on reclaiming the Black body 
                  as a site of power, sovereignty, and collective liberation.
                </p>

                <div className="flex gap-4 flex-wrap">
                  <Link to="/book" className="btn-secondary" data-testid="press-contact-btn">
                    Contact for Press
                  </Link>
                  <Link to="/about" className="text-link" data-testid="press-full-bio-btn">
                    Read Full Bio →
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-brand-deep-green" data-testid="offerings-cta">
        <div className="section-container text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <p className="font-accent text-xl text-text-primary/80 mb-6">
              "Every body deserves the chance to come home."
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-8">
              Ready to Begin?
            </h2>
            <p className="font-body text-text-secondary mb-10">
              Book a brief consultation to discuss which offering might be right for you, 
              your organization, or your community.
            </p>
            <Link to="/book" className="btn-primary" data-testid="offerings-final-cta">
              Book a Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OfferingsPage;
